import { useState, useEffect } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, updateDoc, onSnapshot, deleteField } from 'firebase/firestore';
import { auth, db } from '../firebase';

interface UserUsage {
  month: string;              // server-owned: the month aiProcessingCount belongs to
  aiProcessingCount: number;  // server-owned (detectRegions); firestore.rules block client writes
  manualSessionCount: number;
  manualMonth?: string;       // the month manualSessionCount belongs to (client-owned)
  currentSessionId?: string;
  lastActivity: number;
}

interface UserProfile {
  uid: string;
  email: string;
  subscriptionTier: 'free' | 'premium';
  stripeCustomerId?: string;
  usage: UserUsage;
  createdAt: number;
}

const AI_MONTHLY_LIMIT = 25; // must match functions/src/openai-functions.ts
const MANUAL_MONTHLY_LIMIT = 3;
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * The profile as the UI should see it this month. The stored counters are only
 * reset when next written (the AI one by the server on the next run), so a count
 * left over from a previous month must read as 0 rather than as used-up quota.
 */
export function effectiveProfile(raw: any): UserProfile {
  const month = getCurrentMonth();
  const usage = raw?.usage || {};
  const manualMonth = usage.manualMonth ?? usage.month;
  return {
    ...raw,
    usage: {
      ...usage,
      aiProcessingCount: usage.month === month ? usage.aiProcessingCount || 0 : 0,
      manualSessionCount: manualMonth === month ? usage.manualSessionCount || 0 : 0,
    },
  };
}

/**
 * Whether the user's manual-entry session is still open (started < 30 min ago).
 * Call sites used to check only that a session id existed, and nothing ever
 * cleared it, so one session lasted forever and the monthly cap never bit.
 */
export function manualSessionActive(profile: any): boolean {
  const usage = profile?.usage;
  return !!usage?.currentSessionId && !!usage?.lastActivity && Date.now() - usage.lastActivity <= SESSION_TIMEOUT_MS;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const createUserProfile = async (user: User) => {
    const currentMonth = getCurrentMonth();
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email || '',
      subscriptionTier: 'free',
      usage: {
        month: currentMonth,
        aiProcessingCount: 0,
        manualSessionCount: 0,
        manualMonth: currentMonth,
        lastActivity: Date.now()
      },
      createdAt: Date.now()
    });
  };

  /**
   * Write ONLY the manual-session fields, as dotted paths. The old code wrote the
   * whole profile back from local state, which could roll back an AI count the
   * server had just recorded; the Firestore rules now reject that outright.
   */
  const writeManualUsage = async (fields: { manualSessionCount?: number; currentSessionId?: string | null }) => {
    if (!user) return;
    const update: Record<string, any> = {
      'usage.manualMonth': getCurrentMonth(),
      'usage.lastActivity': Date.now(),
    };
    if (fields.manualSessionCount !== undefined) update['usage.manualSessionCount'] = fields.manualSessionCount;
    if (fields.currentSessionId !== undefined) {
      update['usage.currentSessionId'] = fields.currentSessionId === null ? deleteField() : fields.currentSessionId;
    }
    await updateDoc(doc(db, 'users', user.uid), update);
  };

  // UX pre-check only; detectRegions counts and enforces the real limit.
  const incrementAIUsage = async (): Promise<boolean> => {
    if (!user || !userProfile) return false;
    if (userProfile.subscriptionTier === 'premium') return true;
    return userProfile.usage.aiProcessingCount < AI_MONTHLY_LIMIT;
  };

  const startManualSession = async (): Promise<boolean> => {
    if (!userProfile) return false;
    const now = Date.now();
    const usage = userProfile.usage;
    const expired = !!usage.currentSessionId && !!usage.lastActivity && now - usage.lastActivity > SESSION_TIMEOUT_MS;
    const inSession = !!usage.currentSessionId && !expired;

    if (inSession) {
      await writeManualUsage({}); // refresh lastActivity
      return true;
    }
    if (userProfile.subscriptionTier !== 'premium' && usage.manualSessionCount >= MANUAL_MONTHLY_LIMIT) {
      if (expired) await writeManualUsage({ currentSessionId: null });
      return false;
    }
    await writeManualUsage({
      manualSessionCount: usage.manualSessionCount + 1,
      currentSessionId: `session_${now}`,
    });
    return true;
  };

  const endManualSession = async () => {
    if (!userProfile || !userProfile.usage.currentSessionId) return;
    await writeManualUsage({ currentSessionId: null });
  };

  useEffect(() => {
    let unsubscribeProfile: (() => void) | null = null;
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (unsubscribeProfile) { unsubscribeProfile(); unsubscribeProfile = null; }
      if (!user) {
        setUserProfile(null);
        setLoading(false);
        return;
      }
      // Live listener, so the AI count the server records after each run shows
      // up without a reload and client writes never start from a stale copy.
      unsubscribeProfile = onSnapshot(
        doc(db, 'users', user.uid),
        (snap) => {
          if (snap.exists()) {
            setUserProfile(effectiveProfile(snap.data()));
            setLoading(false);
          } else {
            createUserProfile(user).catch((error) => {
              console.error('Error creating user profile:', error);
              setLoading(false);
            });
          }
        },
        (error) => {
          console.error('Error loading user profile:', error);
          setLoading(false);
        }
      );
    });
    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

  // Only a downgrade can happen client-side (firestore.rules allow no other tier
  // change); upgrades come from the Stripe webhook.
  const updateSubscription = async (subscriptionTier: 'free') => {
    if (!user || !userProfile) {
      throw new Error('User or user profile not available');
    }
    await updateDoc(doc(db, 'users', user.uid), {
      subscriptionTier,
      tierSource: 'manual',
      lastUpdated: Date.now()
    });
  };

  return {
    user,
    userProfile,
    loading,
    incrementAIUsage,
    startManualSession,
    endManualSession,
    updateSubscription
  };
};
